import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";

// Consolidated Import
import UpdateProjectModal from "./update_project_modal.jsx";
import CreateCertificateDetailsModal from "../plans/components/create_certificate_details_modal.jsx";

// Services/Constants
import { PlatformSyscodeString } from "../../constants/platform_syscode.jsx";
import { getUser } from "../../services/local_service.js";
import beveatApiResourceOwner from "../../api/resource_owner_beveat_api.js";
import { commonConfigs, urls } from "../../../config.js";
import { DEVELOPER_PROJ_TABLE_SYSCODE } from "../../constants/developer_proj_table_syscode.jsx";

const ProjectDetailsCard = ({ data, invoicePlanSyscode, isMobile, type = "community" }) => {
  const [isUpdateModalOpen, setUpdateModalOpen] = useState(false);
  const [load, setLoad] = useState(true);
  const [certificates, setCertificates] = useState([]);
  const [isCertModalOpen, setCertModalOpen] = useState(false);
  const navigate = useNavigate();

  const isSandbox = type === "sandbox";
  const env = process.env.REACT_APP_ENV;

  const getTableSyscode = () => {
    if (isSandbox) {
      return env === "production"
        ? DEVELOPER_PROJ_TABLE_SYSCODE.PRODUCTION
        : DEVELOPER_PROJ_TABLE_SYSCODE.STAGING;
    }
    return invoicePlanSyscode;
  };

  const devProjTableSyscode = getTableSyscode();

  // Logic for Sandbox Certificates
  useEffect(() => {
    if (isSandbox) {
      const accountId = getUser()?.account_id;
      const url = urls.resourceOwnerUrl + commonConfigs.apiUrls.getAllDeveloperCertificates(accountId);

      beveatApiResourceOwner.get(url, { headers: commonConfigs.resourceOwnerApiHeader })
        .then((res) => setCertificates(res.data.result.data))
        .catch((err) => console.log("Cert error:", err));
    }
  }, [isSandbox]);

  const handleViewSecret = () => {
    const label = isSandbox ? "Sandbox" : "Project";
    alert(`${label} API Key: ${data.developer_proj_key_value}`);
  };

  const onGetCertificateClick = () => {
    // Standardizing the ID for certification logic
    data.invoice_plan_id = "6557d67e-9dbe-40d3-8ce7-34b3a58cb856";
    const cert = certificates.find(c =>
      c.org_invoice_plan_id == data.invoice_plan_id &&
      c.developer_proj_id == data.developer_proj_id
    );
    if (cert) {
      navigate("/certification", {
        state: {
          certificationPlanId: data.invoice_plan_id,
          environment: "Sandbox",
          devProjId: data.developer_proj_id
        }
      });
    } else {
      setCertModalOpen(true);
    }
  };

  // Shared Styles
  const labelStyle = { color: "black", fontWeight: "bold" };
  const containerStyle = isMobile ? { margin: "0", width: "100%", color: "black" } :
    { marginRight: "10px", marginLeft: "80px", width: "275%", marginTop: "-60px", color: "black" };

  return (
    <div style={containerStyle}>
      {/* Title Section */}
      <div className="mb-4" style={isMobile ? { textAlign: "center", marginBottom: "20px", fontSize: "x-large", fontWeight: "bold", color: "black" } :
        { textAlign: "left", marginBottom: "40px", fontSize: "x-large", marginLeft: "-3.5%", color: "black" }}>
        {isSandbox ? "Sandbox Project Details" : "Community Project"}
      </div>

      {/* Project Display Name */}
      <div className="mb-4 font-bold" style={isMobile ? { textAlign: "center", marginTop: "15px", fontSize: "17px", marginBottom: "20px", color: "black" } :
        { textAlign: "left", marginTop: "35px", fontSize: "17px", color: "black" }}>
        {data.developer_proj_name}
      </div>

      {/* Main Details Card */}
      <div className="bg-white rounded-xl" style={isMobile ? { width: "100%", boxShadow: "2px 4px 8px rgba(0,0,0,0.1)", fontSize: "14px", borderWidth: "0.5px", borderColor: "#e5e7eb", borderStyle: "solid", color: "black" } :
        { width: "100%", boxShadow: "5px 10px #EBECF0", fontSize: "16px", borderWidth: "0.5px", borderColor: "black", borderStyle: "solid", marginTop: "-10px", color: "black" }}>

        <div className="p-4" style={isMobile ? {
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          padding: "16px"
        } : {
          padding: "5px",
          display: "grid",
          gridTemplateAreas: `'text1 text1 text1' 'text3 text3 text3' 'text4 text4 text4' 'text5 text5 text5' 'text6 text6 text6' 'text7 text7 text7' 'button1 button1 button1'`,
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: 4,
          justifyItems: "start"
        }}>

          {/* Project ID */}
          <div style={isMobile ? { textAlign: "left", color: "black" } : { gridArea: "text1", marginTop: "10px", color: "black", textAlign: "left" }}>
            <strong style={labelStyle}>Developer Project Id</strong>: {data.developer_proj_id}
          </div>

          {/* API Key Access */}
          <div style={isMobile ? { textAlign: "left", color: "black" } : { gridArea: "text3", marginTop: "15px", color: "black", textAlign: "left" }}>
            <strong style={labelStyle}>{isSandbox ? "Sandbox" : "Project"} API Key</strong>:
            <button
              onClick={handleViewSecret}
              style={{ backgroundColor: "blue", color: "white", borderRadius: "5px", marginLeft: "20px", padding: "8px 20px", border: "none", fontWeight: "bold" }}
            >
              Click here to view API key
            </button>
          </div>

          {/* Conditional View: Community vs Sandbox */}
          {!isSandbox ? (
            <>
              <div style={isMobile ? { textAlign: "left", color: "black" } : { gridArea: "text4", marginTop: "15px", color: "black", textAlign: "left" }}>
                <strong style={labelStyle}>Project Secret Key</strong>: {data.developer_proj_scrt_key}
              </div>

              <div style={isMobile ? { textAlign: "left", color: "black" } : { gridArea: "text5", marginTop: "20px", color: "black", textAlign: "left" }}>
                <strong style={labelStyle}>Project Platform Type</strong>: {PlatformSyscodeString[data.platform_syscode]} (platform-syscode: {data.platform_syscode})
              </div>
            </>
          ) : (
            <div style={isMobile ? { textAlign: "left", color: "black" } : { gridArea: "text4", marginTop: "20px", color: "black", textAlign: "left" }}>
              <strong style={labelStyle}>API(s) Enabled</strong>: {data.access?.map(p => p.project_name).join(", ") || "None"}
            </div>
          )}

          {/* Website URL */}
          <div style={isMobile ? { textAlign: "left", color: "black" } : { gridArea: "text6", marginTop: "20px", color: "black", textAlign: "left" }}>
            <strong style={labelStyle}>Project Website URL</strong>: {data.developer_proj_website_url || "None"}
          </div>

          {/* API Table (Community Only) */}
          {!isSandbox && (
            <div style={isMobile ? { textAlign: "left", color: "black" } : { gridArea: "text7", marginTop: "20px", color: "black", textAlign: "left", width: "100%" }}>
              <strong style={labelStyle}>Enabled APIs for Project</strong>:
              <div style={isMobile ? { overflowX: "auto" } : { width: "100%" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "10px", color: "black" }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: "8px", color: "black" }}>API Name</th>
                      <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: "8px", color: "black" }}>URL</th>
                      <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: "8px", color: "black" }}>Organization ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.access?.filter(p => !["beveatshop", "payeatfwd", "foodcanopy", "sghawkercentres", "spaerohub"].includes(p.project_name)).map((project, i) => (
                      <tr key={i}>
                        <td style={{ padding: "8px", borderBottom: "1px solid #ddd", color: "black" }}>{project.project_name}</td>
                        <td style={{ padding: "8px", borderBottom: "1px solid #ddd", color: "black" }}>
                          {project.project_url?.split("|").map((url, urlIndex) => (
                            <div key={urlIndex}>{url}</div>
                          )) || "None"}
                        </td>
                        <td style={{ padding: "8px", borderBottom: "1px solid #ddd", color: "black" }}>
                          {project.project_org_id || project.org_id || ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div style={isMobile ? { marginTop: "20px", width: "100%" } : { gridArea: "button1", marginTop: "30px", width: "100%" }}>
            <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: "12px", justifyContent: "flex-start" }}>
              {isSandbox && (
                <button onClick={onGetCertificateClick} className="hover:bg-blue-500 hover:text-white text-blue-500 outline outline-blue-500 rounded-full font-bold px-10 py-2">
                  Get Course Certificate
                </button>
              )}

              <button onClick={() => setUpdateModalOpen(true)} className="hover:bg-blue-500 hover:text-white text-blue-500 outline outline-blue-500 rounded-full font-bold px-10 py-2">
                Update
              </button>

              <button onClick={() => navigate(-1)} className="hover:bg-red-500 hover:text-white text-red-500 outline outline-red-500 rounded-full font-bold px-10 py-2">
                Back
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Dynamic Project Modal */}
      {isUpdateModalOpen && (
        <UpdateProjectModal
          mode={isSandbox ? "sandbox" : "developer"}
          data={data}
          setOpenModal={setUpdateModalOpen}
          load={setLoad}
          tableSyscode={devProjTableSyscode}
        />
      )}

      {/* Certificate Modal */}
      {isCertModalOpen && (
        <CreateCertificateDetailsModal
          setOpenModal={setCertModalOpen}
          planId={data.invoice_plan_id}
          devProjId={data.developer_proj_id}
          environment="Sandbox"
        />
      )}
    </div>
  );
};

export default ProjectDetailsCard;